import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { triggerEvent } from 'utilities/dom';
import LPGlider from 'ui/glider';
import { utagLinkPDP } from 'features/tealium';
import { setGenericEvent } from 'features/tealium/ga4/custom';

import { relationshipItemProp } from '../types';
import { ITEM_GRID_COUNT, ITEM_GRID_PILLS_COUNT } from '../config';
import isMappedTo from '../utility/isMappedTo';
import getFormattedItemLabel from '../utility/getFormattedItemLabel';
import { getPrimaryCategoryFromUtag } from '../utility';

import RelationshipItemDetail from './RelationshipItemDetail';

import './RelationshipItemsSlider.scss';

const RelationshipItemsSlider = ({
	label, activeShortSku, relationshipItems, interfaceType, index
}) => {

	const showRoundedButton = isMappedTo.displayOptions.showRoundedButton(label);

	const noSpaceLabel = label.replace(/\s/g, '');

	const prevBtnRef = React.useRef();
	const nextBtnRef = React.useRef();
	const options = {
		attributes: true,
		childList: true,
		subtree: true
	};
	const [ prevBtnDisabled, setPrevBtnDisabled ] = useState(-1);
	const [ nextBtnDisabled, setNextBtnDisabled ] = useState(-1);

	const showArrows = relationshipItems.length > ITEM_GRID_COUNT;
	const activeSkuIndex = relationshipItems.findIndex((item) => item.shortSku.toUpperCase() === activeShortSku);
	const activeSkuPosition = activeSkuIndex + 1;
	const sliderOptions = {
		slidesToShow: showRoundedButton ? ITEM_GRID_PILLS_COUNT : ITEM_GRID_COUNT,
		slidesToScroll: showRoundedButton ? 1 : ITEM_GRID_COUNT,
		dots: false,
		duration: 0.3,
		arrows: {
			prev: `.prev-${noSpaceLabel}-${index}`,
			next: `.next-${noSpaceLabel}-${index}`
		}
	};

	const getCurrentItem = (relItems) => relItems.filter((item) => item.shortSku.toUpperCase() === activeShortSku);

	const currentItemLabel = useMemo(
		() => getFormattedItemLabel(getCurrentItem(relationshipItems)[0]?.label, label),
		[ label ]
	);
	const [ activeHoverItemLabel, setActiveHoverItemLabel ] = useState('');

	const handleMouseEnter = (heroImageUrl, productName, hoverItemLabel) => {

		setActiveHoverItemLabel(hoverItemLabel);

		triggerEvent.call(window.top.document, 'productRelationships:RelationshipItemDetail:imageMouseOver', {
			heroImageUrl,
			productName
		});

		utagLinkPDP()({
			eventAction: `Hover-${label}-Variation`,
			eventLabel: hoverItemLabel
		});

	};

	const handleMouseLeave = () => {

		setActiveHoverItemLabel('');
		triggerEvent.call(window.top.document, 'productRelationships:RelationshipItemDetail:imageMouseLeave');

	};

	const handleClickPrev = () => {

		utagLinkPDP()({
			eventAction: `Back-${label}-Variation`
		});

		setGenericEvent({
			event_name: 'pdp_variation_back',
			pdp_item_id: activeShortSku,
			pdp_prime_cat: getPrimaryCategoryFromUtag(),
			item_position_number: activeSkuPosition,
			pdp_var_value: `${label}:${currentItemLabel}`
		});

	};

	const handleClickNext = () => {

		utagLinkPDP()({
			eventAction: `Forward-${label}-Variation`
		});

		setGenericEvent({
			event_name: 'pdp_variation_forward',
			pdp_item_id: activeShortSku,
			pdp_prime_cat: getPrimaryCategoryFromUtag(),
			item_position_number: activeSkuPosition,
			pdp_var_value: `${label}:${currentItemLabel}`
		});

	};

	const initSliderAccessibilityObservers = () => {

		const prevBtnObserver = new MutationObserver(() => {

			prevBtnRef.current.getAttribute('aria-disabled') === 'true'
				? setPrevBtnDisabled(-1)
				: setPrevBtnDisabled(0);

		});

		const nextBtnObserver = new MutationObserver(() => {

			nextBtnRef.current.getAttribute('aria-disabled') === 'true'
				? setNextBtnDisabled(-1)
				: setNextBtnDisabled(0);

		});

		prevBtnObserver.observe(prevBtnRef.current, options);
		nextBtnObserver.observe(nextBtnRef.current, options);

		return {
			prevBtnObserver,
			nextBtnObserver
		};

	};

	useEffect(() => {

		utagLinkPDP()({
			eventAction: `Load-${label}-Variation`,
			eventLabel: currentItemLabel,
			nonInteraction: 1
		});

		setGenericEvent({
			event_name: 'pdp_variation_load',
			pdp_item_id: activeShortSku,
			pdp_prime_cat: getPrimaryCategoryFromUtag(),
			item_position_number: activeSkuPosition,
			pdp_var_value: `${label}:${currentItemLabel}`
		});

		const element = document.getElementById(`RelationshipItemGlider-${noSpaceLabel}-${index}`);
		const initialSlideIndex = activeSkuIndex > -1 ? activeSkuIndex : 0;

		element.addEventListener('glider-loaded', function () {

			const glider = LPGlider(this);
			glider.setActiveSlide(initialSlideIndex);

		});

		const { prevBtnObserver, nextBtnObserver } = initSliderAccessibilityObservers();

		const gliderInstance = new LPGlider(element, sliderOptions);
		gliderInstance.init();

		return () => {

			gliderInstance.destroy();
			prevBtnObserver.disconnect();
			nextBtnObserver.disconnect();

		};

	}, []);

	return (
		<div className="RelationshipItemsSlider">
			<strong>
				{label}
			</strong>
			<span className="RelationshipItemsSlider__itemLabel">
				{activeHoverItemLabel || currentItemLabel}
			</span>
			<div
				className={`RelationshipItemsSlider__container ${
					showRoundedButton ? 'RelationshipItemsSlider__roundBtnWrapper' : ''
				}`}
			>
				{showArrows && (
					<button
						className={`glider-arrow glider-prev prev-${noSpaceLabel}-${index}`}
						type="button"
						aria-label="Previous"
						tabIndex={prevBtnDisabled}
						disabled={prevBtnDisabled === -1}
						ref={prevBtnRef}
						onClick={handleClickPrev}
					/>
				)}
				<div id={`RelationshipItemGlider-${noSpaceLabel}-${index}`}>
					{relationshipItems.map((item, i) => (
						<div className="RelationshipItemsSlider__item" key={item.shortSku}>
							<RelationshipItemDetail
								label={label}
								activeShortSku={activeShortSku}
								itemLabel={item.label}
								shortSku={item.shortSku}
								relationshipItemDetail={item.relationshipItemDetail}
								handleMouseEnter={handleMouseEnter}
								handleMouseLeave={handleMouseLeave}
								interfaceType={interfaceType}
								position={i + 1}
							/>
						</div>
					))}
				</div>
				{showArrows && (
					<button
						className={`glider-arrow glider-next next-${noSpaceLabel}-${index}`}
						type="button"
						aria-label="Next"
						tabIndex={nextBtnDisabled}
						disabled={nextBtnDisabled === -1}
						ref={nextBtnRef}
						onClick={handleClickNext}
					/>
				)}
			</div>
		</div>
	);

};

RelationshipItemsSlider.propTypes = {
	label: PropTypes.string,
	activeShortSku: PropTypes.string,
	relationshipItems: PropTypes.arrayOf(relationshipItemProp),
	interfaceType: PropTypes.number,
	index: PropTypes.number
};

export default RelationshipItemsSlider;
